import { SdkContainer, SdkContainerFactories } from './container/SdkContainer';
import { StackScope } from './application/StackScope';
import { SdkConfig } from './domain/types';
import { CaptureFactory, TransportFactory, LoggerFactory } from './infrastructure/factories/CaptureFactory';

// Environment variable detection helper
function detectEnvVars(): Partial<SdkConfig> {
  if (typeof window === 'undefined') return {};
  
  // Check for Vite env vars
  try {
    // @ts-ignore - Vite-specific import.meta.env
    if (typeof import.meta !== 'undefined' && (import.meta as any).env) {
      const env = (import.meta as any).env;
      return {
        workerUrl: env.VITE_STACKSCOPE_WORKER_URL,
        apiKey: env.VITE_STACKSCOPE_API_KEY,
        debug: env.VITE_STACKSCOPE_DEBUG === 'true',
        ...(env.VITE_STACKSCOPE_ENABLED === 'false' && { captureConsole: false, captureNetwork: false, captureInteractions: false })
      };
    }
  } catch {
    // Ignore errors in non-Vite environments
  }
  
  // Check for Next.js/CRA env vars if available through process.env (when bundled)
  try {
    if (typeof process !== 'undefined' && process.env) {
      const env = process.env;
      return {
        workerUrl: env.NEXT_PUBLIC_STACKSCOPE_WORKER_URL || env.REACT_APP_STACKSCOPE_WORKER_URL,
        apiKey: env.NEXT_PUBLIC_STACKSCOPE_API_KEY || env.REACT_APP_STACKSCOPE_API_KEY,
        debug: (env.NEXT_PUBLIC_STACKSCOPE_DEBUG || env.REACT_APP_STACKSCOPE_DEBUG) === 'true'
      };
    }
  } catch {
    // Ignore errors in browser-only environments
  }
  
  return {};
}

let instance: StackScope | null = null;

// Default factories - can be overridden for testing
const createDefaultFactories = (): SdkContainerFactories => ({
  captureFactory: new CaptureFactory(),
  transportFactory: new TransportFactory(),
  loggerFactory: new LoggerFactory()
});

const defaultConfig: Partial<SdkConfig> = {
  batchInterval: 2000,
  maxBatchSize: 50,
  debug: false,
  logLevel: 'error',
  captureConsole: true,
  captureNetwork: true,
  captureInteractions: true,
  captureNavigation: true,
  capturePerformance: true,
  captureVisibility: true,
  captureResources: true
};

export function init(config: Partial<SdkConfig>): StackScope {
  if (instance) {
    console.warn('[StackScope] Already initialized. Ignoring duplicate init.');
    return instance;
  }

  // Auto-construct endpoint from workerUrl if provided
  let resolvedConfig = { ...config };
  
  if (config.workerUrl && !config.endpoint) {
    const workerUrl = config.workerUrl.replace(/\/$/, ''); // Remove trailing slash
    resolvedConfig.endpoint = `${workerUrl}/webhook/browser`;
  }

  // Validate that we have an endpoint
  if (!resolvedConfig.endpoint && !resolvedConfig.workerUrl) {
    throw new Error('[StackScope] either workerUrl or endpoint is required in configuration');
  }

  if (!resolvedConfig.endpoint) {
    throw new Error('[StackScope] failed to resolve endpoint from workerUrl');
  }

  const fullConfig: SdkConfig = {
    ...defaultConfig,
    ...resolvedConfig
  } as SdkConfig;

  const factories = createDefaultFactories();
  const container = new SdkContainer(fullConfig, factories);
  instance = new StackScope(container, fullConfig);
  instance.init();

  return instance;
}

// Convenience function that auto-detects environment variables
export function createStackScope(config?: Partial<SdkConfig>): StackScope {
  const envConfig = detectEnvVars();
  const mergedConfig = { ...envConfig, ...config };
  
  if (!mergedConfig.workerUrl && !mergedConfig.endpoint) {
    console.warn('[StackScope] No workerUrl detected in environment variables. Please provide one in config or set VITE_STACKSCOPE_WORKER_URL');
  }
  
  return init(mergedConfig);
}

export function log(level: 'debug' | 'info' | 'warn' | 'error', msg: string, meta?: Record<string, unknown>): void {
  if (!instance) {
    console.warn('[StackScope] Not initialized. Call init() first.');
    return;
  }
  instance.log(level, msg, meta);
}

export function info(msg: string, meta?: Record<string, unknown>): void {
  log('info', msg, meta);
}

export function warn(msg: string, meta?: Record<string, unknown>): void {
  log('warn', msg, meta);
}

export function error(msg: string, meta?: Record<string, unknown>): void {
  log('error', msg, meta);
}

export function debug(msg: string, meta?: Record<string, unknown>): void {
  log('debug', msg, meta);
}

export function flush(): Promise<void> {
  if (!instance) {
    return Promise.resolve();
  }
  return instance.flush();
}

export function stop(): void {
  if (instance) {
    instance.stop();
    instance = null;
  }
}

export function getSessionId(): string | null {
  return instance ? instance.getSessionId() : null;
}

// Utility functions for common use cases
export function trackUserAction(action: string, details?: Record<string, unknown>): void {
  info(`User action: ${action}`, {
    type: 'interaction',
    action,
    ...details,
    timestamp: new Date().toISOString(),
    sessionId: getSessionId()
  });
}

export function trackPerformance(metric: string, value: number, unit?: string): void {
  info(`Performance metric: ${metric}`, {
    type: 'performance',
    metric,
    value,
    unit: unit || 'ms',
    timestamp: new Date().toISOString(),
    sessionId: getSessionId()
  });
}

export function trackApiCall(url: string, method: string, status: number, duration: number, meta?: Record<string, unknown>): void {
  const level = status >= 400 ? 'error' : status >= 300 ? 'warn' : 'info';
  log(level, `API call: ${method} ${url}`, {
    type: 'network',
    requestUrl: url,
    method,
    status,
    duration,
    timestamp: new Date().toISOString(),
    sessionId: getSessionId(),
    ...meta
  });
}

// Pre-built logger object for easy import
export const logger = {
  debug: (msg: string, meta?: Record<string, unknown>) => debug(msg, meta),
  info: (msg: string, meta?: Record<string, unknown>) => info(msg, meta),
  warn: (msg: string, meta?: Record<string, unknown>) => warn(msg, meta),
  error: (msg: string, meta?: Record<string, unknown>) => error(msg, meta)
};

// Export types for TypeScript users
export type {
  LogLevel,
  LogEntry,
  LogMetadata,
  SdkConfig,
  StackScopeOptions,
  Logger,
  PerformanceMetrics,
  UserAction,
  ApiCallDetails
} from './domain/types';

// Auto-initialize if script tag has data attributes
if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  const script = document.currentScript as HTMLScriptElement;
  
  if (script) {
    const workerUrl = script.getAttribute('data-worker-url');
    const endpoint = script.getAttribute('data-endpoint');
    const apiKey = script.getAttribute('data-api-key');
    const debug = script.getAttribute('data-debug') === 'true';
    
    if (workerUrl || endpoint) {
      init({
        workerUrl: workerUrl || undefined,
        endpoint: endpoint || undefined,
        apiKey: apiKey || undefined,
        debug
      });
    }
  }
}